import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, AlertCircle, Settings, GitBranch, Loader2, Paperclip, X, FileText } from 'lucide-react';
import { incidentAPI, serviceRequestAPI, changeRequestAPI, categoryAPI, applicationAPI, uploadAttachment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LOCATIONS = [
  'HQ - Building A', 'HQ - Building B', 'Branch Office - North', 'Branch Office - South',
  'Data Center - Primary', 'Data Center - DR', 'Remote / Work from Home',
];

export default function NewRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';
  const hasNoRole = !user?.role_name && !user?.role;
  const [reqType, setReqType] = useState('incident');
  const [loading, setLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [applications, setApplications] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [attachPolicy, setAttachPolicy] = useState({ max_attachments: 3, max_attachment_size_mb: 10 });

  const [form, setForm] = useState({
    title: '', description: '', category: '', application: '',
    impact: '', urgency: '', urgency_level: 'standard', location: '', contact_number: '',
    change_type: 'normal', risk_level: 'medium', business_justification: '',
    rollback_plan: '', implementation_plan: '',
  });

  useEffect(() => {
    applicationAPI.list({ page_size: 500 }).then(r => setApplications(r.data?.results || r.data || [])).catch(() => {});
    categoryAPI.listFlat().then(r => setAllCategories(r.data?.results || r.data || [])).catch(() => {});
    // Fetch attachment policy from tenant settings
    import('../services/api').then(({ api: apiClient }) => {
      apiClient.get('/tenants/settings/').then(r => {
        setAttachPolicy({ max_attachments: r.data.max_attachments ?? 3, max_attachment_size_mb: r.data.max_attachment_size_mb ?? 10 });
      }).catch(() => {});
    });
  }, []);

  // When application changes, filter categories
  useEffect(() => {
    if (form.application) {
      const app = applications.find(a => a.id === form.application);
      if (app && app.category_names && app.category_names.length > 0) {
        setFilteredCategories(app.category_names);
      } else {
        // Fallback: fetch from API
        categoryAPI.listFlat().then(r => {
          const data = r.data?.results || r.data || [];
          setFilteredCategories(data.map(c => ({ id: c.id, name: c.name })));
        }).catch(() => setFilteredCategories(allCategories.map(c => ({ id: c.id, name: c.name }))));
      }
      setForm(prev => ({ ...prev, category: '' }));
    } else {
      setFilteredCategories(allCategories.map(c => ({ id: c.id, name: c.name })));
    }
  }, [form.application, applications, allCategories]);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.title || !form.description) { toast.error('Title and Description are required'); return; }
    setLoading(true);
    try {
      const payload = {
        title: form.title, description: form.description,
        location: form.location, contact_number: form.contact_number,
      };
      if (form.category) payload.category = form.category;
      if (form.application) payload.application = form.application;

      let res;
      let ticketType;
      if (reqType === 'incident') {
        if (!form.impact || !form.urgency) { toast.error('Impact and Urgency are required'); setLoading(false); return; }
        payload.impact = form.impact;
        payload.urgency = form.urgency;
        res = await incidentAPI.create(payload);
        ticketType = 'incident';
        toast.success(`Incident ${res.data.ticket_number || ''} created`);
      } else if (reqType === 'sr') {
        payload.urgency_level = form.urgency_level;
        res = await serviceRequestAPI.create(payload);
        ticketType = 'sr';
        toast.success(`Service Request ${res.data.ticket_number || ''} created`);
      } else {
        if (!form.business_justification) { toast.error('Business justification required'); setLoading(false); return; }
        payload.change_type = form.change_type;
        payload.risk_level = form.risk_level;
        const riskToPriority = { critical: 'immediate', high: 'high', medium: 'medium', low: 'low' };
        payload.cr_priority = riskToPriority[form.risk_level] || form.risk_level;
        payload.business_justification = form.business_justification;
        payload.rollback_plan = form.rollback_plan;
        payload.implementation_plan = form.implementation_plan;
        res = await changeRequestAPI.create(payload);
        ticketType = 'cr';
        toast.success(`Change Request ${res.data.ticket_number || ''} created`);
      }

      // Upload attachments if any
      if (attachedFiles.length > 0) {
        const ticketId = res.data.id;
        const uploadResults = await Promise.allSettled(
          attachedFiles.map(file => uploadAttachment(ticketType, ticketId, file))
        );
        const failed = uploadResults.filter(r => r.status === 'rejected').length;
        if (failed > 0) toast.warning(`${attachedFiles.length - failed} file(s) uploaded, ${failed} failed`);
        else toast.success(`${attachedFiles.length} file(s) attached`);
      }

      navigate(`/ticket/${ticketType}/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data;
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail) || 'Failed to create');
    } finally { setLoading(false); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const maxSizeBytes = attachPolicy.max_attachment_size_mb * 1024 * 1024;
    const oversized = files.filter(f => f.size > maxSizeBytes);
    if (oversized.length > 0) {
      toast.error(`File too large: ${oversized.map(f => f.name).join(', ')}. Max size is ${attachPolicy.max_attachment_size_mb} MB.`);
    }
    const validFiles = files.filter(f => f.size <= maxSizeBytes);
    setAttachedFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const newFiles = validFiles.filter(f => !existing.has(f.name + f.size));
      const combined = [...prev, ...newFiles];
      if (combined.length > attachPolicy.max_attachments) {
        toast.warning(`Max ${attachPolicy.max_attachments} attachments allowed. Extra files removed.`);
        return combined.slice(0, attachPolicy.max_attachments);
      }
      return combined;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  const allTypes = [
    { type: 'incident', label: 'Incident', desc: 'Something is broken/down', icon: AlertCircle, borderColor: 'border-rose-500', bgColor: 'bg-rose-50' },
    { type: 'sr', label: 'Service Request', desc: 'I need something', icon: Settings, borderColor: 'border-blue-500', bgColor: 'bg-blue-50' },
    { type: 'cr', label: 'Change Request', desc: 'Modify infrastructure/app', icon: GitBranch, borderColor: 'border-purple-500', bgColor: 'bg-purple-50' },
  ];
  const types = isEndUser ? allTypes.filter(t => t.type !== 'cr') : allTypes;

  return (
    <div data-testid="new-request-page">
      <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
        <PlusCircle className="w-6 h-6 text-sky-500" /> New Request
      </h2>

      {/* Block users with no role */}
      {hasNoRole && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center mb-6">
          <div className="text-rose-700 font-semibold mb-1">Account Not Configured</div>
          <div className="text-sm text-rose-600">Your account has no role assigned. You cannot create tickets. Please contact your administrator to assign a role to your account.</div>
        </div>
      )}

      <div className="bg-white rounded-2xl border p-7 shadow-sm">
        {/* Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3 text-slate-700">Request Type</label>
          <div className={`grid ${isEndUser ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
            {types.map((rt) => {
              const Icon = rt.icon;
              const selected = reqType === rt.type;
              return (
                <div key={rt.type} onClick={() => setReqType(rt.type)}
                  className={`border-2 rounded-xl p-4 cursor-pointer text-center transition-all ${selected ? `${rt.borderColor} ${rt.bgColor}` : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`type-${rt.type}`}>
                  <Icon className={`w-8 h-8 mx-auto mb-2 ${selected ? 'text-sky-600' : 'text-slate-400'}`} />
                  <div className="font-semibold text-slate-800">{rt.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{rt.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Application / System</label>
              <select value={form.application} onChange={e => updateForm('application', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-application">
                <option value="">Select application...</option>
                {applications.map(a => <option key={a.id} value={a.id}>{a.name} ({a.identifier})</option>)}
              </select>
              {form.application && <p className="text-xs text-sky-600 mt-1">Categories filtered for selected application</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Category</label>
              <select value={form.category} onChange={e => updateForm('category', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-category">
                <option value="">Select category...</option>
                {filteredCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Title *</label>
              <input type="text" value={form.title} onChange={e => updateForm('title', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3" placeholder="Brief description" data-testid="field-title" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Description *</label>
              <textarea rows={4} value={form.description} onChange={e => updateForm('description', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3" placeholder="Detailed information..." data-testid="field-description" />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {reqType === 'incident' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Impact *</label>
                  <select value={form.impact} onChange={e => updateForm('impact', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-impact">
                    <option value="">Select impact...</option>
                    <option value="extensive">Extensive / Organization-wide</option>
                    <option value="significant">Significant / Department-wide</option>
                    <option value="moderate">Moderate / Group</option>
                    <option value="minor">Minor / Individual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Urgency *</label>
                  <select value={form.urgency} onChange={e => updateForm('urgency', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-urgency">
                    <option value="">Select urgency...</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </>
            )}
            {reqType === 'sr' && (
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">Request Urgency *</label>
                <select value={form.urgency_level} onChange={e => updateForm('urgency_level', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-sr-urgency">
                  <option value="asap">ASAP (4 hours)</option>
                  <option value="this_week">This Week (3 business days)</option>
                  <option value="standard">Standard (5 business days)</option>
                  <option value="no_rush">No Rush (10 business days)</option>
                </select>
              </div>
            )}
            {reqType === 'cr' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Change Type *</label>
                  <select value={form.change_type} onChange={e => updateForm('change_type', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-change-type">
                    <option value="standard">Standard (Pre-approved)</option>
                    <option value="normal">Normal (Requires approval)</option>
                    <option value="emergency">Emergency (Expedited)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Priority / Risk Level *</label>
                  <select value={form.risk_level} onChange={e => updateForm('risk_level', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-risk-level">
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Business Justification *</label>
                  <textarea rows={2} value={form.business_justification} onChange={e => updateForm('business_justification', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3" placeholder="Why is this change needed?" data-testid="field-justification" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Location</label>
              <select value={form.location} onChange={e => updateForm('location', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white" data-testid="field-location">
                <option value="">Select location...</option>
                {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">
                Attachments
                <span className="ml-2 text-xs font-normal text-slate-400">
                  (max {attachPolicy.max_attachments} files, {attachPolicy.max_attachment_size_mb} MB each)
                </span>
              </label>
              <div
                className={`bg-slate-50 border border-dashed rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors ${
                  attachedFiles.length >= attachPolicy.max_attachments
                    ? 'border-slate-200 opacity-60 cursor-not-allowed'
                    : 'border-slate-300'
                }`}
                onClick={() => attachedFiles.length < attachPolicy.max_attachments && fileInputRef.current?.click()}
                data-testid="attachment-dropzone"
              >
                <input ref={fileInputRef} type="file" multiple accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff" className="hidden" onChange={handleFileSelect} />
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Paperclip className="w-4 h-4" />
                  {attachedFiles.length >= attachPolicy.max_attachments
                    ? `Max ${attachPolicy.max_attachments} files reached`
                    : `Click to attach files (${attachedFiles.length}/${attachPolicy.max_attachments})`
                  }
                </div>
              </div>
              {attachedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5">
                      <FileText className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                      <span className="text-slate-700 truncate flex-1">{file.name}</span>
                      <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)}KB</span>
                      <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} className="text-slate-400 hover:text-rose-500 ml-1">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700">Contact Number</label>
              <input type="tel" value={form.contact_number} onChange={e => updateForm('contact_number', e.target.value)} className="w-full border border-slate-300 rounded-xl px-4 py-3" placeholder="+91 (XXX) XXX-XXXX" data-testid="field-phone" />
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3 pt-6 border-t">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-700 px-6 py-3" data-testid="cancel-btn">Cancel</button>
          <button onClick={handleSubmit} disabled={loading || hasNoRole} className="bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 text-white px-8 py-3 rounded-xl font-medium flex items-center gap-2" data-testid="submit-request-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
